import { useCallback } from "react";
import { Button, Group, Table, Text } from "@mantine/core";
import type {
  ReportReviewMatrixPathologist,
  ReportReviewMatrixPrefix,
} from "../../../services/admin/reportReviewSettings.service";
import { ReportReviewRequirementCell } from "./ReportReviewRequirementCell";

interface ReportReviewPrefixRowProps {
  prefix: ReportReviewMatrixPrefix;
  pathologists: ReportReviewMatrixPathologist[];
  optionalReviewKeys: Set<string>;
  disabled: boolean;
  onCellChange: (
    prefixId: string,
    pathologistUserId: string,
    required: boolean
  ) => void;
  onPrefixChange: (prefixId: string, required: boolean) => void;
}

export function ReportReviewPrefixRow({
  prefix,
  pathologists,
  optionalReviewKeys,
  disabled,
  onCellChange,
  onPrefixChange,
}: ReportReviewPrefixRowProps) {
  const handleRequire = useCallback(() => {
    onPrefixChange(prefix.id, true);
  }, [onPrefixChange, prefix.id]);
  const handleMakeOptional = useCallback(() => {
    onPrefixChange(prefix.id, false);
  }, [onPrefixChange, prefix.id]);

  return (
    <Table.Tr>
      <Table.Th
        scope="row"
        pos="sticky"
        left={0}
        bg="var(--mantine-color-body)"
        miw={220}
        style={{ zIndex: 1 }}
      >
        <Text fw={700}>{prefix.code}</Text>
        <Group gap={4} mt={4} wrap="nowrap">
          <Button size="compact-xs" onClick={handleRequire} disabled={disabled}>
            Require review
          </Button>
          <Button
            size="compact-xs"
            variant="light"
            onClick={handleMakeOptional}
            disabled={disabled}
          >
            Make optional…
          </Button>
        </Group>
      </Table.Th>
      {pathologists.map((pathologist) => (
        <Table.Td key={pathologist.id}>
          <ReportReviewRequirementCell
            prefixId={prefix.id}
            prefixCode={prefix.code}
            pathologistUserId={pathologist.id}
            pathologistName={pathologist.fullName}
            reviewRequired={
              !optionalReviewKeys.has(`${prefix.id}:${pathologist.id}`)
            }
            disabled={disabled}
            onChange={onCellChange}
          />
        </Table.Td>
      ))}
    </Table.Tr>
  );
}
